import React, { Suspense, useEffect } from "react";
import { usePromise } from "react-use";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useAppContext } from "../../hooks/useAppContext";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useDisableImageContextMenu } from "../../hooks/useDisableImageContextMenu";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { Automation } from "../automation";
import { DialogQueue } from "../dialog-queue";
import { OfflineDialog } from "../dialogs/offline-dialog";
import { GlobalLoading } from "../global-loading";
import { Header } from "../header";
import { NavigationBar } from "../navigation-bar";
import { OrientationBlocker, OrientationBlockerContent } from "../orientation-blocker";
import { PreviewBox } from "../preview-box";
import { SlideshowPreview } from "../slideshow-preview";
import { Snackbar } from "../snackbar";
import { TopToolbar } from "../top-toolbar";
import { useAppUnload } from "./useAppUnload";

import "../../variables.css";
import "../../reset.css";
import "../../scrollbar.css";

import "./app.css";
import { useSetDefinitionFeatures } from "../../hooks/useDefinitionFeatures";
import { useLoginManager } from "../../hooks/useLoginManager";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { usePerformanceMonitoring } from "../../hooks/usePerformaceMonitoring";
import { useResetDefinitionForDesign } from "../../hooks/useResetDefinitionForDesign";
import { useResetDefinitionForOptions } from "../../hooks/useResetDefinitionForOptions";
import { useReuseZonesForPages } from "../../hooks/useReuseZonesForPages";
import { useReuseZonesForTemplates } from "../../hooks/useReuseZonesForTemplates";
import { TooltipLayer } from "../tooltip";
import { labels } from "./labels";
import { useFeatureFlag, useFeatureFlagToggle } from "../../hooks/useFeatureFlags";
import { useKeyDown } from "../../hooks/useKeyboard";

const BottomMenu = React.lazy(
	() => import(/* webpackChunkName: "express-bottom-menu" */ "../bottom-menu"),
);

export function App() {
	// Extract info from context
	const appContext = useAppContext();
	const { busyBlocker } = useBusyBlockerContext();
	const { createFatalErrorHandler, createErrorHandler } = useErrorHandling();
	const loginManager = useLoginManager();
	const featureAddMapZones = useFeatureFlagToggle("add2MapZonesPerPage");
	const featureDefaultMapStyle = useFeatureFlag("addDefaultMapStyle", "");
	const featureDefaultMapLocation = useFeatureFlag("addDefaultMapLocation", "");

	useDisableImageContextMenu();
	useDocumentTitle();
	useSetDefinitionFeatures();
	useReuseZonesForTemplates();
	useReuseZonesForPages();
	useResetDefinitionForDesign();
	useResetDefinitionForOptions();

	const {
		editor,
		getFontSizeCorrectorAsync,
		localizedContent,
		platform: { queryString },
	} = appContext;

	const resolveIfMounted = usePromise();

	// Prevent default behavior on save
	// The real save handler is conditional in another component
	useKeyDown(
		(event) => {
			if (event.key === "s" && (event.ctrlKey || event.metaKey)) {
				event.preventDefault();
			}
		},
		{
			capture: true, // Also execute for items that call stopPropagation
			enableOnFormTags: true, // Example: needed to disable save on during text edit
			ignoreTrap: true, // Always prevent default browser keys. Example: Ctrl+S
		},
	);

	usePerformanceMonitoring();
	const navigateToUrl = useNavigateToUrl();

	// Onload of App
	useEffect(
		() => {
			const fetchKeysAsync = async () => {
				// Preload all labels
				const keys = eXpress.creator
					.getLabelKeys(labels) // Flatten nested structure
					.map(
						(
							labelRequestOrString, // Only the keys needed
						) =>
							eXpress.utils.isString(labelRequestOrString)
								? labelRequestOrString
								: labelRequestOrString.label,
					);

				await localizedContent.prefetchLabelsAsync(keys).catch(createErrorHandler());
			};

			const getDefinitionRequestFromQueryString = ():
				| eXpress.core.DefinitionRequest
				| undefined => {
				const productVariantCodeString = queryString.getQueryStringParameter("pvc");
				const productVariantCode = productVariantCodeString
					? eXpress.ProductVariantCode.fromString(productVariantCodeString)
					: undefined;
				const predefinedProductNameString = queryString.getQueryStringParameter("des");
				const predefinedProductName = predefinedProductNameString
					? eXpress.PredefinedProductName.fromString(predefinedProductNameString)
					: undefined;

				return predefinedProductName && productVariantCode
					? {
							predefinedProductName,
							productVariantCode,
					  }
					: undefined;
			};

			const loadDefinitionAsync = async () => {
				const definitionId = queryString.getQueryStringParameter("def");
				const definitionRequest: eXpress.core.DefinitionRequest | undefined = definitionId
					? eXpress.DefinitionId.fromString(definitionId)
					: // Create Definition
					  getDefinitionRequestFromQueryString();

				if (!definitionRequest) return;
				if (definitionId) {
					await loginManager.waitForLogin();
				}

				let error: eXpress.Exception | undefined = undefined;
				const definitionData = editor
					.loadDefinitionAsync(
						definitionRequest,
						getFontSizeCorrectorAsync,
						// React.StrictMode will run some functions twice causing definition to be loaded twice (Only run at start error)
						// TODO: (React 18) components should be resilient to effects being mounted and destroyed multiple times
						true,
					)
					.then((definition) => {
						if (featureAddMapZones) {
							// Set only first 2 photo zone as map zone
							definition.getUsedPages().forEach((pageZone) => {
								const zone = pageZone.photoZones.at(0);
								if (!zone) return;
								if (!zone.metaData) zone.metaData = {};
								zone.metaData.AllowedPhotoSourceTypes = "map";

								const zone2 = pageZone.photoZones.at(1);
								if (!zone2) return;
								if (!zone2.metaData) zone2.metaData = {};
								zone2.metaData.AllowedPhotoSourceTypes = "map";
							});
						}
						if (featureDefaultMapStyle) {
							definition.metaData ??= {};
							definition.metaData["Map.MapStyle"] = featureDefaultMapStyle;
						}
						if (featureDefaultMapLocation) {
							definition.metaData ??= {};
							definition.metaData["Map.Location"] = featureDefaultMapLocation;
						}

						return definition;
					})
					.catch((err: unknown) => {
						error = err as eXpress.Exception;
					});

				const [definition] = await Promise.all([definitionData, fetchKeysAsync()]);
				// Prevent throwing unneccesairy error just before redirecting to login
				if (!definition) {
					if (error) throw error;
					throw new Error("No product specified in querystring to load.");
				}
			};

			resolveIfMounted(busyBlocker.while(loadDefinitionAsync)).catch((e: unknown) => {
				// When the page is refreshed (or browser back to this page) it can happen that the browser uses a cached page
				// If the user has no or expired authenication cookie, we can start the application without being authenticated
				if (e instanceof eXpress.api.NotAuthenticatedException) {
					editor.logger.log(
						"No authentication cookie (accId) found anymore. Redirecting to login page.",
					);
					navigateToUrl(
						`/login.aspx?returnurl=${encodeURIComponent(window.location.href)}`,
						{
							waitForPendingUploads: "none",
						},
					);
					// Don't throw error for redirect/operation abort since this means the user is already redirected or navigated away
				} else if (
					!(
						e instanceof eXpress.RedirectException ||
						e instanceof eXpress.OperationAbortedException
					)
				) {
					createFatalErrorHandler()(e);
				}
			});
		},
		// this function should NEVER be rerun, all dependencies should be immutable
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	useEffect(() => {
		const onPageShow = (event: PageTransitionEvent) => {
			const historyTraversal =
				event.persisted ||
				(typeof window.performance !== "undefined" &&
					// eslint-disable-next-line @typescript-eslint/no-deprecated
					window.performance.navigation.type === 2);

			if (historyTraversal) {
				document.location.reload();
			}
		};

		window.addEventListener("pageshow", onPageShow);

		return () => {
			window.removeEventListener("pageshow", onPageShow);
		};
	}, []);

	useAutoSave();
	useAppUnload();

	return (
		<>
			<div className="app">
				<SlideshowPreview />
				<Header />
				<div className="app__below-header">
					<div className="app__content">
						<TopToolbar />
						{/* Box measuring the location for the preview */}
						<PreviewBox />
						<NavigationBar />
					</div>
					<Suspense fallback={<GlobalLoading />}>
						{/* fullscreen blocking was needed: CRE-3348 */}
						<BottomMenu />
					</Suspense>
				</div>
			</div>
			<Snackbar />
			<DialogQueue />
			<OfflineDialog />
			<OrientationBlocker>
				<OrientationBlockerContent />
			</OrientationBlocker>
			<TooltipLayer />
			<Automation />
		</>
	);
}
